/**
 * @lodex-pro/web-portal/routes/TicketsList — ticket history table.
 *
 * Phase 15 / Task 15.4 (DEC-153) — the server clamps `from` silently to the history the
 * plan includes and says so in `meta`; the page then shows a banner. OPP PR 6: the limit
 * is the OP_TICKETS feature's, in days (FREE 30; none = all history, BASIC and up), so every
 * plan gets the date filters and the server keeps them inside its window.
 * Pagination: 50 per page, offset-based (server enforces max 200).
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { usePortalMe, usePortalTickets } from '../hooks/useOperatorPortal';
import { ExportButtons } from '../components/ExportButtons';
import { PortalDataError } from '../components/PortalDataError';
import { ScopeNotice } from '../components/ScopeNotice';
import { formatNumber } from '@lodex-pro/shared/number-format';

const PAGE_SIZE = 50;

function formatDate(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

export function TicketsList(): React.JSX.Element {
  const me = usePortalMe();
  const canFilter = me.data !== undefined;

  const [offset, setOffset] = React.useState(0);
  const [from, setFrom] = React.useState<string>('');
  const [to, setTo] = React.useState<string>('');

  const { data, isLoading, isError, error } = usePortalTickets({
    limit: PAGE_SIZE,
    offset,
    ...(canFilter && from !== '' ? { from } : {}),
    ...(canFilter && to !== '' ? { to } : {}),
  });

  const tickets = data?.tickets ?? [];
  const total = data?.total ?? 0;
  const clampedDays = data?.meta.clampedDays ?? null;

  return (
    <main style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px',
          marginBottom: '12px',
        }}
      >
        <h1 style={{ margin: 0 }}>Tickets</h1>
        {/* Operator Reports (0241) — exactly this page's filters, every matching ticket. */}
        <ExportButtons
          page="tickets"
          me={me.data}
          params={{
            ...(canFilter && from !== '' ? { from } : {}),
            ...(canFilter && to !== '' ? { to } : {}),
          }}
        />
      </div>
      <ScopeNotice me={me.data} />

      {clampedDays !== null && (
        <div
          role="status"
          style={{
            padding: '12px 16px',
            marginBottom: '16px',
            background: 'rgba(123, 150, 232, 0.18)',
            color: 'var(--color-accent-blue-dark)',
            borderRadius: '12px',
            fontSize: '14px',
          }}
        >
          Showing the last {clampedDays} days, the ticket history your company&apos;s plan includes.
        </div>
      )}

      {canFilter && (
        <div
          aria-label="Ticket filters"
          style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'flex-end' }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: '13px' }}>
            <span>From</span>
            <input
              type="date"
              value={from}
              onChange={(e): void => {
                setFrom(e.target.value);
                setOffset(0);
              }}
              style={{
                padding: '6px',
                background: 'var(--color-bg-tertiary)',
                color: 'var(--color-text-primary)',
                border: '1px solid var(--color-border-secondary)',
                borderRadius: '10px',
              }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: '13px' }}>
            <span>To</span>
            <input
              type="date"
              value={to}
              onChange={(e): void => {
                setTo(e.target.value);
                setOffset(0);
              }}
              style={{
                padding: '6px',
                background: 'var(--color-bg-tertiary)',
                color: 'var(--color-text-primary)',
                border: '1px solid var(--color-border-secondary)',
                borderRadius: '10px',
              }}
            />
          </label>
        </div>
      )}

      {isLoading && <p role="status">Loading tickets…</p>}
      {isError && <PortalDataError error={error} fallback="Could not load tickets." />}

      {data !== undefined && (
        <>
          <table
            aria-label="Tickets"
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              background: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border-primary)',
            }}
          >
            <thead>
              <tr style={{ background: 'var(--color-bg-tertiary)' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Haul ID</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Captured at</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Site</th>
                <th style={{ textAlign: 'right', padding: '8px 12px' }}>Volume (BBL)</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Hauler</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Disposal</th>
              </tr>
            </thead>
            <tbody>
              {tickets.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      padding: '16px',
                      textAlign: 'center',
                      color: 'var(--color-text-tertiary)',
                    }}
                  >
                    No tickets in this range.
                  </td>
                </tr>
              ) : (
                tickets.map((t) => (
                  <tr key={t.haulId} style={{ borderTop: '1px solid var(--color-border-primary)' }}>
                    <td style={{ padding: '8px 12px' }}>
                      <Link
                        to={`/portal/tickets/${encodeURIComponent(t.haulId)}`}
                        style={{ color: 'var(--color-link)' }}
                      >
                        {t.haulId}
                      </Link>
                    </td>
                    <td style={{ padding: '8px 12px' }}>{formatDate(t.capturedAt)}</td>
                    <td style={{ padding: '8px 12px' }}>{t.siteName ?? '—'}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      {formatNumber(t.volumeBbl)}
                      {/* DEC-302 — volumeBbl is now the figure the invoice carries, so a
                          corrected haul reads differently here than it used to. The
                          marker says WHY, with the gauged figure in its title, rather
                          than silently changing a number an operator may have exported
                          last week. Nothing is rendered on an uncorrected ticket. */}
                      {t.reviewedVolumeBbl !== null && (
                        <abbr
                          title={`Corrected in review. Gauged ${formatNumber(t.capturedVolumeBbl)} BBL.`}
                          aria-label={`Corrected in review. Gauged ${formatNumber(t.capturedVolumeBbl)} BBL.`}
                          style={{
                            marginLeft: '4px',
                            color: 'var(--color-warning)',
                            cursor: 'help',
                            textDecoration: 'none',
                          }}
                        >
                          *
                        </abbr>
                      )}
                    </td>
                    <td style={{ padding: '8px 12px' }}>{t.haulerName}</td>
                    <td style={{ padding: '8px 12px' }}>
                      {t.deliveredToDisposalFacilityName ?? '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '12px',
              fontSize: '13px',
              color: 'var(--color-text-tertiary)',
            }}
          >
            <span>
              Showing {formatNumber(tickets.length === 0 ? 0 : offset + 1)}–
              {formatNumber(offset + tickets.length)} of {formatNumber(total)}
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={(): void => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0}
                style={{ padding: '6px 12px', cursor: offset === 0 ? 'not-allowed' : 'pointer' }}
              >
                Previous
              </button>
              <button
                type="button"
                onClick={(): void => setOffset(offset + PAGE_SIZE)}
                disabled={offset + tickets.length >= total}
                style={{
                  padding: '6px 12px',
                  cursor: offset + tickets.length >= total ? 'not-allowed' : 'pointer',
                }}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </main>
  );
}
